import {
  Signature,
  SignatureDisplayName,
  SignatureEnum,
} from "pmt-types/src/enums/Signature.ts";
import { Renewal, RenewalSchema } from "pmt-types/src/models/Renewal.ts";
import { useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import LoadingModal from "../../components/app/loadingModal.tsx";
import CoveragePicker from "../../components/form/coveragePicker.tsx";
import EnumSelect from "../../components/form/enumSelect.tsx";
import FormInput from "../../components/form/formInput.tsx";
import PmtForm from "../../components/form/pmtForm.tsx";
import FormRoot from "../../components/form/style/formRoot.tsx";
import FormSection from "../../components/form/style/formSection.tsx";
import TextArea from "../../components/form/textArea.tsx";
import LoadingSpinner from "../../components/loadingSpinner.tsx";
import NotFoundPage from "../../global/notFoundPage.tsx";
import { useMutationAddRenewal } from "../../hooks/mutations/useMutationAddRenewal.ts";
import { QueryKeys } from "../../hooks/queries/queryKeys.ts";
import { useQueryGetProjectById } from "../../hooks/queries/useQueryGetProjectById.ts";
import { useQueryGetSourceById } from "../../hooks/queries/useQueryGetSourceById.ts";

export default function AddRenewal() {
  const navigate = useNavigate();
  const { sourceId, projectId, renewalId } = useParams();
  const queryClient = useQueryClient();

  const { data: project, isFetching: isProjectFetching } =
    useQueryGetProjectById(sourceId, projectId);
  const { data: source, isFetching: isSourceFetching } = useQueryGetSourceById(
    project?.source.id,
  );

  const { mutate, isLoading: isSubmitting } = useMutationAddRenewal();

  if (renewalId == null) {
    return <NotFoundPage />;
  }

  if (isSourceFetching || isProjectFetching) {
    return <LoadingSpinner />;
  }

  if (source == null || project == null) {
    return <NotFoundPage />;
  }

  const defaultRenewalValues: Partial<Renewal> = {
    id: uuidv4(),
    name: `Renewal for ${project.name}`,
    source,
    project,
    signature: project.signature,
    descriptionOfOperations: `The Certificate Holder is an Insured under the Owner Controlled Insurance Program as specified in the contract documents. Project Name: ${project.name}`,
    policies: {
      generalLiability: source.generalLiability,
      excessLiabilities: source.excessLiabilities ?? [],
    },
  };

  return (
    <>
      <PmtForm<Renewal>
        schema={RenewalSchema}
        pageTitle={`${source.name} / ${project.name} / New Renewal`}
        defaultValues={defaultRenewalValues}
        submitMutation={mutate}
        onSave={async () => {
          await queryClient.invalidateQueries({
            queryKey: [QueryKeys.GetEnrolleeCertificates],
          });
          await queryClient.invalidateQueries({
            queryKey: [QueryKeys.GetProjectRenewals, projectId],
          });
          navigate(
            `/sources/${sourceId}/projects/${projectId}/details?tab=renewals`,
          );
        }}
        onCancel={() => navigate(-1)}
        isSubmitting={isSubmitting}
        renderForm={(form) => {
          form.register("source");
          form.register("project");
          return (
            <FormRoot>
              <FormSection label={"Details"}>
                <FormInput
                  name="effectiveDate"
                  label="Effective Date"
                  type="date"
                />
                <FormInput
                  name="expirationDate"
                  label="Expiration Date"
                  type="date"
                />
                <EnumSelect
                  name="signature"
                  label="Signature"
                  data={Signature.options}
                  formatOption={(option) =>
                    SignatureDisplayName[option as SignatureEnum]
                  }
                />
                <TextArea
                  name={"descriptionOfOperations"}
                  label={"Description of Operations"}
                />
                <CoveragePicker name={"policies"} source={source} />
              </FormSection>
            </FormRoot>
          );
        }}
      />
      <LoadingModal
        isOpen={isSubmitting}
        message={
          "Generating the renewal, this might take a minute! Please wait..."
        }
      />
    </>
  );
}
